// Fix #15 (2026-05-28): Onboarding hints for first-time users.
//
// A tooltip pinned to a target element via a CSS selector. Shows once per
// device per hint id (persisted in localStorage). Click anywhere
// dismisses the hint and writes the dismissal flag.
//
// Visual: soft gold pill with an arrow pointing at the target.

import { useEffect, useState, useLayoutEffect } from 'react';

const STORAGE_PREFIX = 'storiz.onboarding.';

function hintKey(id: string) {
  return `${STORAGE_PREFIX}${id}.dismissed`;
}

function isDismissed(id: string): boolean {
  try { return localStorage.getItem(hintKey(id)) === '1'; } catch { return true; }
}

function markDismissed(id: string) {
  try { localStorage.setItem(hintKey(id), '1'); } catch { /* quota / SSR */ }
}

type Placement = 'top' | 'bottom' | 'left' | 'right';

export function OnboardingHint({
  id,
  target,
  text,
  placement = 'top',
  /** Delay before showing the hint so the target has time to mount. */
  showAfterMs = 600,
  /** Auto-dismiss after this long (no click required). 0 = never. */
  autoDismissMs = 0,
}: {
  id: string;
  target: string;
  text: string;
  placement?: Placement;
  showAfterMs?: number;
  autoDismissMs?: number;
}) {
  // Track dismissal in state so a click anywhere unmounts the tooltip
  // immediately. Initial value reads localStorage to skip the hint if it
  // was dismissed in a previous session.
  const [dismissed, setDismissed] = useState(() => isDismissed(id));
  const [anchor, setAnchor] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [ready, setReady] = useState(false);

  // Wait for the target to appear in the DOM.
  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      const el = document.querySelector(target) as HTMLElement | null;
      if (el) {
        const r = el.getBoundingClientRect();
        setAnchor({ x: r.left, y: r.top, w: r.width, h: r.height });
        if (showAfterMs > 0) {
          setTimeout(() => { if (!cancelled) setReady(true); }, showAfterMs);
        } else {
          setReady(true);
        }
        return;
      }
      tries += 1;
      if (tries < 30) setTimeout(tick, 200);
    };
    tick();
    return () => { cancelled = true; };
  }, [target, dismissed, showAfterMs]);

  // Update anchor on resize/scroll so the hint follows the target.
  useLayoutEffect(() => {
    if (!ready || dismissed) return;
    const reflow = () => {
      const el = document.querySelector(target) as HTMLElement | null;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setAnchor({ x: r.left, y: r.top, w: r.width, h: r.height });
    };
    window.addEventListener('resize', reflow);
    window.addEventListener('scroll', reflow, true);
    return () => {
      window.removeEventListener('resize', reflow);
      window.removeEventListener('scroll', reflow, true);
    };
  }, [target, ready, dismissed]);

  // Click anywhere dismisses.
  useEffect(() => {
    if (!ready || dismissed) return;
    const dismiss = () => { markDismissed(id); setDismissed(true); };
    document.addEventListener('pointerdown', dismiss, { capture: true });
    let timer: number | undefined;
    if (autoDismissMs > 0) {
      timer = window.setTimeout(dismiss, autoDismissMs);
    }
    return () => {
      document.removeEventListener('pointerdown', dismiss, { capture: true } as EventListenerOptions);
      if (timer) clearTimeout(timer);
    };
  }, [id, ready, dismissed, autoDismissMs]);

  if (dismissed || !ready || !anchor) return null;

  // Position the pill near the target, with an arrow pointing at it.
  const pad = 10;
  let style: React.CSSProperties = {
    position: 'fixed',
    zIndex: 10000,
    pointerEvents: 'none',
    background: 'linear-gradient(135deg, #C9A24A, #B8902F)',
    color: '#fff',
    padding: '8px 14px',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 500,
    boxShadow: '0 6px 20px rgba(201, 162, 74, 0.45), 0 2px 6px rgba(0,0,0,0.15)',
    maxWidth: 280,
    lineHeight: 1.35,
    whiteSpace: 'normal',
    textAlign: 'center',
  };

  let arrowStyle: React.CSSProperties | null = null;

  const cx = anchor.x + anchor.w / 2;
  const cy = anchor.y + anchor.h / 2;

  if (placement === 'top') {
    style = {
      ...style,
      left: cx,
      top: anchor.y - pad,
      transform: 'translate(-50%, -100%)',
    };
    arrowStyle = {
      position: 'fixed',
      left: cx,
      top: anchor.y - pad,
      transform: 'translate(-50%, -2px) rotate(45deg)',
      width: 10, height: 10,
      background: '#B8902F',
      zIndex: 10000,
      pointerEvents: 'none',
    };
  } else if (placement === 'bottom') {
    style = {
      ...style,
      left: cx,
      top: anchor.y + anchor.h + pad,
      transform: 'translate(-50%, 0)',
    };
    arrowStyle = {
      position: 'fixed',
      left: cx,
      top: anchor.y + anchor.h + pad - 8,
      transform: 'translate(-50%, 0) rotate(45deg)',
      width: 10, height: 10,
      background: '#C9A24A',
      zIndex: 10000,
      pointerEvents: 'none',
    };
  } else if (placement === 'left') {
    style = {
      ...style,
      left: anchor.x - pad,
      top: cy,
      transform: 'translate(-100%, -50%)',
    };
    arrowStyle = {
      position: 'fixed',
      left: anchor.x - pad,
      top: cy,
      transform: 'translate(-2px, -50%) rotate(45deg)',
      width: 10, height: 10,
      background: '#B8902F',
      zIndex: 10000,
      pointerEvents: 'none',
    };
  } else {
    style = {
      ...style,
      left: anchor.x + anchor.w + pad,
      top: cy,
      transform: 'translate(0, -50%)',
    };
    arrowStyle = {
      position: 'fixed',
      left: anchor.x + anchor.w + pad - 8,
      top: cy,
      transform: 'translate(0, -50%) rotate(45deg)',
      width: 10, height: 10,
      background: '#C9A24A',
      zIndex: 10000,
      pointerEvents: 'none',
    };
  }

  return (
    <>
      {arrowStyle && <div style={arrowStyle} aria-hidden="true" />}
      <div role="status" aria-live="polite" style={style}>{text}</div>
    </>
  );
}
